import React, {useState, useEffect} from 'react';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import {useParams} from 'react-router-dom';
import {Accordion, AccordionSummary, Button} from '@material-ui/core';
import {useSelector, useDispatch} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {
  getEnrollmentApplicationsDetail,
  getEnrollmentErrorDetails,
  changeEnrollmentApplicationErrorDetailFilters,
  enrollmentApplicationErrorDetailFiltersEmpty,
  changeSort,
  clearSort,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {toEither, toPageEither, fromResult} from 'Core/models/Async';
import {pipe} from 'fp-ts/lib/pipeable';
import './GenelcoSpreadsheetUnsuccessfulEmployeeRecords.sass';
import GenelcoSpreadsheetFileErrorDetailsDialog from './GenelcoSpreadsheetFileErrorDetailsDialog';
import {withStringFilterProperties, withInequalityFilterProperties} from 'Shared/components/PagedView/Filter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import * as E from 'fp-ts/lib/Either';
import {sequenceT} from 'fp-ts/lib/Apply';
import {renderAsync} from 'Shared/components/RenderAsync';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIcon} from 'Shared/components/Toolbar/ToolbarHeader';
import * as O from 'fp-ts/lib/Option';

const GenelcoSpreadsheetUnsuccessfulEmployeeRecords = () => {
  const {fileId} = useParams();
  const dispatch = useDispatch();
  const [openErrorBox, setOpenErrorBox] = useState(false);
  const handleClose = () => {
    setOpenErrorBox(false);
    dispatch(changeEnrollmentApplicationErrorDetailFilters(enrollmentApplicationErrorDetailFiltersEmpty));
  };
  const handleOpen = () => {
    setOpenErrorBox(true);
    dispatch(changeEnrollmentApplicationErrorDetailFilters(enrollmentApplicationErrorDetailFiltersEmpty));
  };

  const {
    enrollmentApplicationsDetails,
    enrollmentFileErrorDetails,
    enrollmentApplicationErrorDetailFilters,
    sort,
  } = useSelector((state: ReducerState) => state.enrollmentFileProcessing);
  useBreadcrumbs(
    'Unsuccessful Employee Records',
    EnrollmentRouteConstants.ENROLLMENT_GENELCOSHEET_STATUS_UNSUCCESSFUL_EMPLOYEE_RECORDS(fileId!)
  );
  const changeFilter = withValueChangedHook(
    enrollmentApplicationErrorDetailFilters,
    changeEnrollmentApplicationErrorDetailFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: enrollmentApplicationErrorDetailFilters,
  };

  useEffect(() => {
    dispatch(getEnrollmentApplicationsDetail(fileId!));
    dispatch(changeEnrollmentApplicationErrorDetailFilters(enrollmentApplicationErrorDetailFiltersEmpty));
    dispatch(
      getEnrollmentErrorDetails(fileId!, {...enrollmentApplicationErrorDetailFilters, id: fileId!}, O.toUndefined(sort))
    );
    dispatch(clearSort());
  }, [fileId]);

  const combinedRequests = sequenceT(E.either)(
    toPageEither(enrollmentFileErrorDetails),
    toEither(enrollmentApplicationsDetails)
  );
  return pipe(
    combinedRequests,
    E.fold(renderAsync, ([enrollmentFileErrorDetails, enrollmentFiles]) => (
      <>
        <ModularHost>
          <div className="genelco-spreadsheet-file-error-review__error-details-action">
            <Button className="error-details" variant="contained" onClick={handleOpen} color="primary">
              Error Details
            </Button>
          </div>
          <ToolbarHeader title="Unsuccessful Employee Records">
            <ExportToolbarHeaderIcon
              title="Export Unsuccessful Employee Records"
              downloadedFileName="Application details"
              extension={FileExtension.CSV}
              relativeFileLocation={`/enrollmentFiles/error/details/${addFilterParameters({
                ...enrollmentApplicationErrorDetailFilters,
                id: fileId!,
                exportToCsv: true,
              })}`}
              totalRecordCount={0}
            />
          </ToolbarHeader>
          <div className="genelco-spreadsheet-file-error-review">
            <div className="summary-line-info first-summary-line">
              <Accordion className="summary-line-info__expansion-panel">
                <AccordionSummary>
                  <div className="summary-line-info__expansion-panel__icon-container">
                    <i className="icon-container__file-icon"></i>
                  </div>
                  <div className="summary-line-info__expansion-panel__file-details">
                    {enrollmentFiles.map(enrollmentFile => (
                      <>
                        <div className="summary-line-info__expansion-panel__file-details--content">
                          <span className="title">File Name:</span>
                          <span className="value">
                              {enrollmentFile.fileName}
                          </span>
                        </div>
                        <div className="summary-line-info__expansion-panel__file-details--content">
                          <span className="title">Uploaded:</span>
                          <span className="value">{toFormattedDateTime(enrollmentFile.fileUploadedDate!)}</span>
                        </div>
                        <div className="summary-line-info__expansion-panel__file-details--content">
                          <span className="title">Application Count:</span>
                          <span className="value">{enrollmentFile.rowCount}</span>
                        </div>
                        <div className="summary-line-info__expansion-panel__file-details--content">
                          <span className="title">Applications with Errors:</span>
                          <span className="value">
                            {enrollmentFile.invalidAuditCount! +
                              enrollmentFile.invalidRecordCount! +
                              enrollmentFile.groupWebComparisonErrorCount!}
                          </span>
                        </div>
                        <GenelcoSpreadsheetFileErrorDetailsDialog
                          dialogOpen={openErrorBox}
                          dialogClose={handleClose}
                          employeeErrorDetails={enrollmentFileErrorDetails!.result}
                          enrollmentFileId={enrollmentFile.id!}
                          fileName={enrollmentFile.fileName!}
                          applicationCount={enrollmentFile.rowCount!}
                          applicationErrorCount={
                            enrollmentFile.invalidAuditCount! +
                            enrollmentFile.invalidRecordCount! +
                            enrollmentFile.groupWebComparisonErrorCount!
                          }
                          uploadedDate={toFormattedDateTime(enrollmentFile.fileUploadedDate!)}
                        />
                      </>
                    ))}
                  </div>
                </AccordionSummary>
              </Accordion>
            </div>
            <PagedView
              pagedViewKey="genelcoSpreadsheetFileError"
              data={fromResult(enrollmentFileErrorDetails!)}
              sort={O.toUndefined(sort)}
              onChangeSort={sort => dispatch(changeSort(sort))}
              loadMore={() => {}}
            >
              {columnProps => (
                <>
                  <Column
                    {...columnProps}
                    title="First Name"
                    filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
                    sortColumnKey="firstName"
                    default
                  >
                    {employeeErrorDetail => employeeErrorDetail.firstName}
                  </Column>
                  <Column
                    {...columnProps}
                    title="Last Name"
                    filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
                    sortColumnKey="lastName"
                    default
                  >
                    {employeeErrorDetail => employeeErrorDetail.lastName}
                  </Column>
                  <Column
                    {...columnProps}
                    title="SSN"
                    filterProperties={withStringFilterProperties(filterProperties, 'ssn')}
                    sortColumnKey="maskedSSN"
                    default
                  >
                    {employeeErrorDetail => employeeErrorDetail.maskedSSN}
                  </Column>
                  <Column
                    {...columnProps}
                    title="Request Type"
                    filterProperties={withStringFilterProperties(filterProperties, 'requestType')}
                    sortColumnKey="requestType"
                    default
                  >
                    {employeeErrorDetail => employeeErrorDetail.requestType?.name}
                  </Column>
                  <Column
                    {...columnProps}
                    title="Error Count"
                    filterProperties={withInequalityFilterProperties(filterProperties, 'errorCount')}
                    sortColumnKey="totalErrorCount"
                    default
                  >
                    {employeeErrorDetail => employeeErrorDetail.totalErrorCount!}
                  </Column>
                </>
              )}
            </PagedView>
          </div>
        </ModularHost>
      </>
    ))
  );
};

export default GenelcoSpreadsheetUnsuccessfulEmployeeRecords;
